import { useSafeChart } from '../../components/Chart/ChartProvider';
import {
  useReportParameters,
  useTabContentConfig,
} from '../../components/PageConfigProvider/PageConfigProvider';
import { useFilterContext } from '../contexts/FilterProvider';
import {
  type AllFilterDefinitionsMapType,
  type AllFilterHandlersMapType,
  DEFAULT_ACTIVE_FILTERS,
  getFilterDefinition,
  getFilterHandler,
  WRAPPED_ONLY_FILTER_IDS,
} from '../engine/filterRegistry';
import type { FilterConfig } from '../engine/types';
import {
  type Filter,
  FilterId,
  isSegmentedFilter,
  type SegmentedFilter,
} from '../types/filters';
import {
  buildParameterContext,
  getBaseFilterState,
  mergeFilterStates,
} from './filterParameterUtils';
import { useAvailableDateRangeWithTrialLag } from './useAvailableDateRangeWithTrialLag';
import { useViewDateGranularity } from './useViewDateGranularity';

export const useEffectiveFilters = ({
  viewGranularity: viewGranularityOverride,
  includeDisabledFilters = false,
}: {
  viewGranularity?: 'week' | 'month';
  includeDisabledFilters?: boolean;
} = {}): Filter[] => {
  const { activeFilterMap, filterState } = useFilterContext();
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();

  const { item } = useSafeChart();

  const {
    config,
    dateFilterId: viewDateFilterId,
    hasFilter,
    hasSecondaryFilter,
  } = useTabContentConfig();

  const plotParameters = item?.plot?.plotParameters ?? [];
  const reportParameters = useReportParameters();
  const paramContext = buildParameterContext(
    plotParameters,
    reportParameters,
    filterState
  );

  const viewGranularityDerived = useViewDateGranularity();
  const viewGranularity = viewGranularityOverride ?? viewGranularityDerived;

  const effectiveFilters = (() => {
    const result: Filter[] = [];

    activeFilterMap.forEach((currentFilterState, filterId) => {
      // Skip wrapped-only filters UNLESS they are segmented filters
      const isWrappedOnly = WRAPPED_ONLY_FILTER_IDS.has(filterId);
      const isCurrentlySegmented = isSegmentedFilter(currentFilterState);
      if (isWrappedOnly && !isCurrentlySegmented) {
        return;
      }

      const hasFilterInTemplate = hasFilter(filterId);
      const isDefaultActive = DEFAULT_ACTIVE_FILTERS.some(
        (filter) => filter.id === filterId
      );

      const shouldIncludeFilter =
        includeDisabledFilters || hasFilterInTemplate || isDefaultActive;

      if (!shouldIncludeFilter) {
        return;
      }

      const definition = getFilterDefinition(filterId);
      const handler = getFilterHandler(filterId);

      if (!definition || !handler) {
        console.warn(`No definition or handler for filterId: ${filterId}`);
        return;
      }

      const specificHandler =
        handler as AllFilterHandlersMapType[keyof AllFilterHandlersMapType];
      const specificDefinition =
        definition as AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType];
      const baseState = getBaseFilterState(
        filterId,
        currentFilterState,
        paramContext
      );
      // An Industry report parameter is folded into the Company filter only on
      // pages that don't carry Industry themselves — where it would otherwise
      // be dropped. Folding it in as well as emitting it would apply the same
      // industries twice and put them in a Company chip that can't show them.
      const foldIndustryIntoCompany =
        filterId === FilterId.COMPANY && !hasFilter(FilterId.INDUSTRY);

      const additionsForThisFilter = paramContext.additionalParameters
        .filter(
          (p) =>
            p.filterId === filterId ||
            (foldIndustryIntoCompany && p.filterId === FilterId.INDUSTRY)
        )
        .map((p) => p.filterMap.get(p.filterId))
        .filter((f): f is Filter => f !== undefined);

      const specificCurrentFilterState = mergeFilterStates(
        baseState,
        additionsForThisFilter
      ) as AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType];

      // If filter is SegmentedFilter, check isActive on wrappedFilter
      let isActive: boolean;
      let filterToCheck: Filter;
      const isSegmented = isSegmentedFilter(specificCurrentFilterState);
      if (isSegmented) {
        // For SegmentedFilter, check isActive on the wrappedFilter
        // Type assertion needed because TypeScript can't narrow the union type properly
        const segmentedFilter =
          specificCurrentFilterState as SegmentedFilter<Filter>;
        filterToCheck = segmentedFilter.wrappedFilter;
        isActive = (
          specificHandler.isActive as (
            state: AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType]
          ) => boolean
        )(
          // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
          filterToCheck as any
        );
      } else {
        filterToCheck = specificCurrentFilterState;
        isActive = (
          specificHandler.isActive as (
            state: AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType]
          ) => boolean
        )(specificCurrentFilterState);
      }

      if (isActive) {
        const filterConfig: FilterConfig = {
          availableDateRange,
          viewPrimary: config.primaryFilter?.id,
          viewGranularity,
          viewDateFilterId,
          dateRangeDefault: config.dateRangeDefault,
        };

        // If SegmentedFilter, get effective filter from wrappedFilter, then reconstruct SegmentedFilter
        let effectiveFilter: Filter;
        if (isSegmented) {
          const effectiveWrappedFilter = (
            specificHandler.getEffectiveFilter as (
              state: AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType],
              def: AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType],
              fConfig: FilterConfig
            ) => AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType]
          )(
            // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
            filterToCheck as any,
            specificDefinition,
            filterConfig
          );

          // Reconstruct SegmentedFilter with effective wrappedFilter
          // Type assertion needed because TypeScript can't narrow the union type properly
          const segmentedFilter =
            specificCurrentFilterState as SegmentedFilter<Filter>;
          effectiveFilter = {
            ...segmentedFilter,
            wrappedFilter: effectiveWrappedFilter,
          } as Filter;
        } else {
          effectiveFilter = (
            specificHandler.getEffectiveFilter as (
              state: AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType],
              def: AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType],
              fConfig: FilterConfig
            ) => AllFilterDefinitionsMapType[keyof AllFilterDefinitionsMapType]
          )(specificCurrentFilterState, specificDefinition, filterConfig);
        }

        // When includeDisabledFilters is true, exclude overridden filters
        // that are NOT in secondaryFilters (would be shown as disabled chips).
        if (
          includeDisabledFilters &&
          !hasSecondaryFilter(filterId) &&
          filterId === viewDateFilterId &&
          config.dateRangeDefault
        ) {
          return;
        }

        result.push(effectiveFilter);
      }
    });

    return result;
  })();

  return effectiveFilters;
};
